import { useCallback, useEffect, useMemo, useState } from "react"
import { api } from "../api/client"
import { useApp } from "./AppState"
import { usePolling } from "../lib/usePolling"

/**
 * Loads a project with its tags, its people and the issues this viewer can see.
 *
 * Mutations refetch rather than patch local state: the server owns the edit
 * lock and the visibility rules, so its answer is the only one worth rendering.
 */
export function useProject(projectId) {
	const { toast, refreshProjects } = useApp()
	const [project, setProject] = useState(null)
	const [tags, setTags] = useState([])
	const [issues, setIssues] = useState([])
	const [people, setPeople] = useState([])
	const [teams, setTeams] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const reload = useCallback(
		async ({ quiet = false } = {}) => {
			if (!projectId) return
			if (!quiet) setLoading(true)
			try {
				const [detail, list] = await Promise.all([
					api.project(projectId),
					api.issues(projectId)
				])
				setProject(detail.project)
				setTags(detail.tags)
				setPeople(detail.people || [])
				setTeams(detail.teams || [])
				setIssues(list.issues)
				setError(null)
			} catch (cause) {
				setError(cause)
				if (!quiet) toast(cause.message, "error")
			} finally {
				setLoading(false)
			}
		},
		[projectId, toast]
	)

	useEffect(() => {
		reload()
	}, [reload])

	// Issues raised or answered by someone else appear within a tick.
	usePolling(() => reload({ quiet: true }), undefined, Boolean(projectId))

	const afterMutation = useCallback(async () => {
		await reload({ quiet: true })
		refreshProjects()
	}, [reload, refreshProjects])

	const peopleById = useMemo(() => {
		const index = {}
		people.forEach((person) => {
			index[person.user_id] = person
		})
		return index
	}, [people])

	return {
		project,
		tags,
		setTags,
		issues,
		people,
		teams,
		setTeams,
		peopleById,
		loading,
		error,
		reload,
		afterMutation
	}
}

/** Index tags by id so an issue's `tag_ids` can be rendered. */
export function indexTags(tags) {
	const index = {}
	tags.forEach((tag) => {
		index[tag.tag_id] = tag
	})
	return index
}
